import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from '../../../../utils/debounce';
import { ensureArray } from '../../../../utils/ensureArray';

import styles from './ScrollSnap.module.scss';

type ScrollSnapScrollSnapAlignProp = 'start' | 'center' | 'end';
type ScrollSnapAlignmentProp = 'left' | 'center';

type ScrollSnapProps = {
  /**
   * The set of children to render inside of the scrollable area.
   */
  children: React.ReactNode;
  /**
   * The width in px of each child item (currently only same-width items are supported).
   */
  itemWidth: number;
  /**
   * Handler function to respond to scroll events in the scrollable area.
    @default () => {}
   */
  onScroll?: (pageIndex: number) => void;
  /**
   * The desired page index to scroll the scrollable area to. The initial value is used as the starting page,
   * and any updates to this value will trigger an update to the scrollable area, scrolling to that page.
    @default 0
   * */
  pageIndex?: number;
  /**
   * The `scrollSnapAlign` property applied to the child item wrapper.
   * @default 'start'
   */
  scrollSnapAlign?: ScrollSnapScrollSnapAlignProp;
  /**
   * The gap in pixels between items in the scrollable area.
   * @default 0
   */
  gap?: number;
  /**
   * Which way to align the items in the scroll snap.
   * @default 'left'
   */
  alignment?: ScrollSnapAlignmentProp;
};

// Creates an array of the snap points for all the pages, based on `scrollSnapAlign`.
// e.g. if `scrollSnapAlign` is `start`, this is an array of all th "`start` points of the items.
const getSnapPoints = (opts: {
  itemWidth: number;
  pageCount: number;
  scrollSnapAlign: ScrollSnapScrollSnapAlignProp;
  gap: number;
}) => {
  const { itemWidth, pageCount, scrollSnapAlign, gap } = opts;
  const snapPointGetter = {
    start: (_v: any, i: number) => (itemWidth + gap) * i, // the left side of each item
    center: (_v: any, i: number) => itemWidth / 2 + i * (itemWidth + gap / 2), // halfway between each item
    end: (_v: any, i: number) => itemWidth + i * (itemWidth + gap), // the right side of each item
  }[scrollSnapAlign];
  return Array.from({ length: pageCount }).map(snapPointGetter);
};

// Creates an array of the snapping thresholds for all the pages, based on `scrollSnapAlign`.
// e.g. if the user scrolls to a point between `snapThresholds[i]` and `snapThresholds[i+1]`
// the browser will snap the scrollable area to page `i`.
const getSnapThresholds = (opts: {
  itemWidth: number;
  pageCount: number;
  scrollSnapAlign: ScrollSnapScrollSnapAlignProp;
  gap: number;
  clientWidth: number;
}): number[] => {
  const { itemWidth, scrollSnapAlign, pageCount, gap, clientWidth } = opts;
  const snappingPoints = getSnapPoints(opts);
  const width = itemWidth + gap;
  // this is the point at which the user can no longer scroll to the right
  const stoppingPoint = width * pageCount - clientWidth;
  const snapThresholdGetter = {
    start: (acc: number[], _v: any, i: number, src: any[]) => {
      let point;
      if (i === 0) {
        point = 0;
      } else if (i === 1) {
        point = width / 2;
      } else if (i === src.length - 1) {
        point = width * src.length - (clientWidth - width);
      } else {
        point = acc[i - 1] + width;
      }

      // if the calculated threshold is past the point at which we can no longer scroll
      // then we "correct" it to about half the distance between the stopping point and the last
      // snapping point we saw
      if (point >= stoppingPoint) {
        // offset must be at least 1, so that we cover cases where subsequent snapping points are
        // actually far past the stopping point (e.g. if 3 pages fit inside `clientWidth`)
        const offset = Math.max((stoppingPoint - snappingPoints[i - 1]) / 2, 1);
        point = stoppingPoint - offset;
      }

      acc[i] = point;
      return acc;
    },
    center: (acc: number[], _v: any, i: number, src: any[]) => {
      if (i === 0) {
        acc[i] = 0;
      } else if (i === 1) {
        acc[i] = width + width / 4;
      } else if (i === src.length - 1) {
        acc[i] = width * src.length - width * 1.25;
      } else {
        acc[i] = width * i;
      }
      return acc;
    },
    end: (acc: number[], _v: any, i: number) => {
      if (i === 0) {
        acc[i] = 0;
      } else if (i === 1) {
        acc[i] = width * 1.75;
      } else {
        acc[i] = i * width + width / 2;
      }
      return acc;
    },
  }[scrollSnapAlign];
  return Array.from({ length: pageCount }).reduce(
    snapThresholdGetter,
    []
  ) as number[];
};

const DEBOUNCE_MS = 50;

/**
 * @private
 * Renders a scrollable area with scroll snap points.
 */
const ScrollSnap = ({
  children,
  itemWidth,
  alignment = 'left',
  gap = 0,
  onScroll = undefined,
  pageIndex = 0,
  scrollSnapAlign = 'start',
}: ScrollSnapProps) => {
  const childArray = ensureArray(children);
  const pageCount = childArray.length;
  const rootRef = useRef<HTMLDivElement>(null);
  const clientWidth = rootRef.current?.clientWidth || itemWidth;
  const scrollableAreaRef = useRef<HTMLDivElement>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(pageIndex);
  const snapThresholds = getSnapThresholds({
    pageCount,
    itemWidth,
    scrollSnapAlign,
    gap,
    clientWidth,
  });

  // updates the scrollable area to scroll to a new desired `pageIndex` location
  // debounce to avoid a feedback loop where `pageIndex` changing triggers `onScroll` which changes `pageIndex`...
  const updateScrollableAreaDebounced = useCallback(
    debounce(
      (opts?: {
        itemWidth: number;
        pageCount: number;
        pageIndex: number;
        rootRef: typeof rootRef;
        scrollableAreaRef: typeof scrollableAreaRef;
        scrollSnapAlign: ScrollSnapScrollSnapAlignProp;
      }) => {
        if (!opts || !opts.scrollableAreaRef.current || !opts.rootRef.current) {
          return;
        }
        // offsets the scrollLeft according to the desired scrollSnapAlign
        const scrollLeftOffset = {
          start: 0,
          center: (opts.rootRef.current?.clientWidth || 0) / 2,
          end: opts.rootRef.current?.clientWidth || 0,
        }[opts.scrollSnapAlign];
        const snapPoints = getSnapPoints({
          pageCount: opts.pageCount,
          itemWidth: opts.itemWidth,
          scrollSnapAlign: opts.scrollSnapAlign,
          gap,
        });
        // sets scrollLeft to the corresponding snap point minus any offset
        if (opts.scrollableAreaRef.current) {
          opts.scrollableAreaRef.current.scrollLeft =
            snapPoints[opts.pageIndex] - scrollLeftOffset;
        }
      },
      DEBOUNCE_MS
    ),
    []
  );

  // hooks up our debounced callback to useEffect
  useEffect(() => {
    // dependencies must be passed into this scope, otherwise their values will be stale
    return updateScrollableAreaDebounced({
      itemWidth,
      pageCount,
      pageIndex,
      rootRef,
      scrollableAreaRef,
      scrollSnapAlign,
    });
  }, [
    updateScrollableAreaDebounced,
    itemWidth,
    pageCount,
    pageIndex,
    rootRef,
    scrollableAreaRef,
    scrollSnapAlign,
  ]);

  // scroll handler which calls back to the parent with any changes in `pageIndex`
  const onScrollInternal = () => {
    const { current } = scrollableAreaRef;
    const scrollLeft = current?.scrollLeft || 0;
    // the point on the x-axis which determines the closest snapping point
    // i.e. scrollLeft + either the start, center, or end of the scrollable area
    const cursor = {
      start: scrollLeft,
      center: scrollLeft + clientWidth / 2,
      end: scrollLeft + clientWidth,
    }[scrollSnapAlign];
    // finds which snapping thresholds that the cursor is currently between
    const closestPoint = snapThresholds.reduce(
      (acc, point) => (cursor > point ? point : acc),
      0
    );
    const closestPageIndex = snapThresholds.indexOf(closestPoint);

    // we don't want to trigger another `useEffect` right after scrolling
    // so we invoke the debounced function with no arguments immediately, beginning the debounce period
    updateScrollableAreaDebounced();

    // only fire the `onScroll` handler when the page has actually changed, or else we get a lot of churn
    if (onScroll && currentPageIndex !== closestPageIndex) {
      onScroll(closestPageIndex);
    }
    setCurrentPageIndex(closestPageIndex);
  };

  // sets a fixed width based on number of pages + gap to create a wider scrolling surface
  const scrollingSurfaceWidth =
    pageCount * itemWidth + gap * Math.max(pageCount - 1, 0);
  const scrollingSurfaceWidthInPx = `${scrollingSurfaceWidth}px`;
  const itemWidthInPx = `${itemWidth}px`;

  return (
    <div className={styles.root} ref={rootRef} data-zds>
      <div
        className={styles.scrollableArea}
        onScroll={onScrollInternal}
        ref={scrollableAreaRef}
        tabIndex={0}
        data-zds
      >
        <div
          className={styles.scrollingSurface}
          data-alignment={alignment}
          data-zds
          style={{
            '--_zds-scroll-snap-surface-width': scrollingSurfaceWidthInPx,
          }}
        >
          {childArray.map((child, i) => {
            return (
              <span
                className={styles.snappable}
                key={`${i}-snappable`}
                data-zds
                style={{
                  '--_zds-scroll-snap-snappable-align': scrollSnapAlign,
                  '--_zds-scroll-snap-snappable-width': itemWidthInPx,
                }}
              >
                {child}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { ScrollSnap, getSnapPoints, getSnapThresholds };
